import React, { useState, useEffect, useRef } from "react";
import {
  View,
  TextInput,
  Animated,
  StyleSheet,
  Text,
  KeyboardTypeOptions,
  Platform,
  Image,
  TouchableOpacity,
  ActivityIndicator
} from "react-native";
import Colors from "../../../constants/colors";
import { inputAssistDisabledProps } from "./FloatingTextInputComponent";
import RoundedCloseIcon from "../../../../../../assets/images/RoundedCloseIcon.svg";

/** `right` + `width` of Apply + small gap so typed text does not run under the button. */
const PROMO_INPUT_PADDING_RIGHT_APPLY = 10 + 70 + 12;
/** Space for clear icon + margin (matches FloatingTextInput clear affordance). */
const PROMO_INPUT_PADDING_RIGHT_CLEAR = 48;

type Props = {
  label?: string;
  value?: string;
  isAutoFocus?: boolean;
  testID?: string;
  onChangeText: (text: string) => void;
  editable?: boolean;
  keyboardType?: KeyboardTypeOptions;
  errorText?: string | null;
  maxLength?: number;
  handleApplyPromoCode?: () => void;
  clearPromoCode?: () => void;
  activityIndicatorStatus?: boolean;
  isPromocodeApplied?: boolean;
};

const PromocodeTextInputComponent: React.FC<Props> = ({
  label,
  value,
  isAutoFocus,
  testID,
  onChangeText,
  editable = true,
  keyboardType,
  errorText,
  maxLength,
  handleApplyPromoCode,
  clearPromoCode,
  activityIndicatorStatus,
  isPromocodeApplied
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const animated = useRef(new Animated.Value(value ? 1 : 0)).current;

  const isReviewMode =
  testID === "sendReviewAndEdit" || testID === "sendFinalReview";

  const inputState = !editable
  ? "disabled"
  : errorText
  ? "error"
  : !value
  ? "default"
  : isFocused
  ? "typing"
  : "filled";

  useEffect(() => {
    Animated.timing(animated, {
      toValue: isFocused || !!value ? 1 : 0,
      duration: 180,
      useNativeDriver: false,
    }).start();
  }, [isFocused, value]);

  const labelColor = errorText
    ? Colors.errorRedColor
    : isFocused
    ? Colors.black
    : isReviewMode
    ? Colors.subTitleColor
    : Colors.infoSubValue;

  const labelStyle = {
  position: "absolute" as const,
  left: 12,
  top: animated.interpolate({
    inputRange: [0, 1],
    outputRange: [18, 10],
  }),
  fontSize: animated.interpolate({
    inputRange: [0, 1],
    outputRange: [16, 14],
  }),
  color: Colors.subTitleColor,
  fontFamily: "Rubik",
  // backgroundColor: "transparent",
};

  const borderColor = errorText
    ? Colors.errorRedColor
    : isFocused
    ? Colors.black
    : Colors.textInputBackground;

  const applyButtonStyle = [
    styles.promoApplyButton,
    inputState === "disabled" && { backgroundColor: Colors.borderGrey },
    inputState === "error" && { borderColor: Colors.titleTextColor },
    inputState === "filled" && { backgroundColor: Colors.titleTextColor },
    inputState === "typing" && { backgroundColor: Colors.titleTextColor },
  ];

  const applyTextStyle = [
    styles.applyText,
    inputState === "filled" && { color: Colors.white },
    inputState === "disabled" && { color: Colors.white},
    inputState === "error" && { color: Colors.white },
    inputState === "typing" && { color: Colors.white },
  ];

  return (
    <View style={{ marginVertical: 8 }}>
      <View style={[styles.container, { borderColor}]}>
        <Animated.Text style={labelStyle}>{label}</Animated.Text>
        <TextInput
          testID={testID}
          autoFocus={isAutoFocus}
          value={value}
          style={[
            styles.input,
            {
              color: isPromocodeApplied ? Colors.successInfoGreen : Colors.black,
              paddingRight: isPromocodeApplied
                ? PROMO_INPUT_PADDING_RIGHT_CLEAR
                : PROMO_INPUT_PADDING_RIGHT_APPLY,
            },
          ]}
          placeholderTextColor={Colors.infoSubValue}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChangeText={onChangeText}
          editable={editable}
          keyboardType={keyboardType}
          maxLength={maxLength}
          {...inputAssistDisabledProps}
        />
        {isPromocodeApplied ? (
          <TouchableOpacity
            testID="clearButton"
            style={styles.clearButton}
            onPress={clearPromoCode}
          >
            <RoundedCloseIcon width={24} height={24} />
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            style={applyButtonStyle}
            disabled={!editable || activityIndicatorStatus}
            testID="promoApplyButton"
            onPress={handleApplyPromoCode}
          >
            {activityIndicatorStatus ? (
              <ActivityIndicator
                size="small"
                color={Colors.white}
                style={{ paddingVertical: 4, paddingHorizontal: 6 }}
              />
            ) : (
              <Text style={applyTextStyle}>Apply</Text>
            )}
          </TouchableOpacity>
        )}
      </View>
      {errorText && (
        <Text style={styles.errorText}>
          {errorText}
        </Text>
      )}
    </View>
  );
};

export default PromocodeTextInputComponent;

const styles = StyleSheet.create({
  container: {
    height: 56,
    borderWidth: 1,
    borderRadius: 8,
    backgroundColor: Colors.textInputBackground,
    position: "relative",
  },
  input: {
    height: "100%",
    paddingHorizontal: 12,
    paddingTop: Platform.OS === "web" ? 14 : 2,
    lineHeight: Platform.OS === "web" ? 24 : 22,
    fontSize: 16,
    fontWeight: "400",
    fontFamily: "Rubik",
    // ...(Platform.OS === 'web' && {
    //   outlineWidth: 0,
    //   outline: 'none',
    //   border: 'none',
    //   boxShadow: 'none',
    //   backgroundColor: 'transparent',
    //   // WebkitBoxShadow: '0 0 0 1000px white inset',
    //   WebkitTextFillColor: Colors.black,
    // } as any),
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } as any : {}),
  },
  errorText: {
    fontSize: 14,
    fontFamily: 'Rubik',
    fontWeight: '400',
    lineHeight: 20,
    color: Colors.errorRedColor,
    marginTop: 6,
  },
  clearButton: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: [{ translateY: -10 }], // center vertically
  },
  promoApplyButton: {
    position: "absolute",
    right: 10,
    top: "40%",
    transform: [{ translateY: -10 }],
    borderRadius: 8,
    borderColor: Colors.applyText,
    backgroundColor: Colors.borderGrey,
    width: 70,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center'
  },
  applyText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '400',
    color: Colors.white,
    lineHeight: 18,
  },
});